import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { ArrowLeft, Clock, ArrowRight, Hash, Sparkles } from 'lucide-react';
import Navbar from '../components/Navbar';
import BlogCTABanner from '../components/BlogCTABanner';
import { ContactSection, Footer } from '../components/Footer';
import { blogsApi, absoluteUploadUrl } from '../api/blogs';

const InsertMidPostCTA = ({ body }) => {
  // Insert the CTA banner after roughly the first 1/3 of the content by splitting on '\n\n'
  const parts = (body || '').split(/\n\n+/);
  if (parts.length < 4) return null;
  const cutoff = Math.max(2, Math.floor(parts.length / 3));
  return {
    before: parts.slice(0, cutoff).join('\n\n'),
    after: parts.slice(cutoff).join('\n\n'),
  };
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [related, setRelated] = useState([]);
  const [popularTags, setPopularTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    (async () => {
      try {
        const b = await blogsApi.getPublic(slug);
        setBlog(b);
        document.title = `${b.seo_title || b.title} | EasyWorks`;
        if (b.seo_description || b.excerpt) {
          let m = document.querySelector('meta[name="description"]');
          if (!m) { m = document.createElement('meta'); m.setAttribute('name','description'); document.head.appendChild(m); }
          m.setAttribute('content', b.seo_description || b.excerpt);
        }
        // Fetch all others to compute related + popular tags
        const all = await blogsApi.listPublic();
        const others = all.filter((x) => x.slug !== b.slug);
        const rel = others
          .map((x) => ({ ...x, overlap: (x.tags || []).filter((t) => (b.tags || []).includes(t)).length }))
          .sort((a, z) => z.overlap - a.overlap || new Date(z.published_at) - new Date(a.published_at))
          .slice(0, 3);
        setRelated(rel);
        // Popular tags: aggregate across ALL posts
        const counts = {};
        all.forEach((x) => (x.tags || []).forEach((t) => { counts[t] = (counts[t] || 0) + 1; }));
        const pop = Object.entries(counts).sort((a, z) => z[1] - a[1]).slice(0, 10).map(([t]) => t);
        setPopularTags(pop);
      } catch (err) {
        setNotFound(true);
      } finally { setLoading(false); }
    })();
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAF2E8]"><div className="w-8 h-8 border-4 border-[#F39200] border-t-transparent rounded-full animate-spin" /></div>;
  if (notFound) return <Navigate to="/blog" replace />;
  if (!blog) return null;

  // Extract H2 headings for "In this article" summary/TOC
  const headings = [...(blog.body || '').matchAll(/^##\s+(.+)$/gm)].map((m) => m[1]);
  const split = InsertMidPostCTA({ body: blog.body });

  return (
    <div className="min-h-screen bg-[#FAF2E8] font-[Inter]" data-testid={`blog-post-${slug}`}>
      <Navbar />
      <article className="bg-white py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-[#F39200]"><ArrowLeft size={14}/> All posts</Link>
          {blog.tags?.[0] && <div className="mt-6 text-xs uppercase tracking-wider text-[#F39200] font-semibold">{blog.tags[0]}</div>}
          <h1 className="mt-2 text-3xl md:text-5xl font-extrabold text-neutral-900 leading-[1.1]">{blog.title}</h1>
          {blog.excerpt && <p className="mt-4 text-lg text-neutral-600">{blog.excerpt}</p>}
          <div className="mt-6 flex items-center gap-3 text-sm text-neutral-500">
            {blog.author_photo_url && <img src={blog.author_photo_url} alt="" className="w-8 h-8 rounded-full" />}
            {blog.author_name && <span className="font-semibold text-neutral-800">{blog.author_name}</span>}
            <span className="inline-flex items-center gap-1"><Clock size={12}/> {new Date(blog.published_at || blog.created_at).toLocaleDateString(undefined, { day:'numeric', month:'short', year:'numeric' })}</span>
          </div>
          {blog.cover_image_url && <img src={absoluteUploadUrl(blog.cover_image_url)} alt="" className="mt-8 w-full aspect-[16/9] object-cover rounded-2xl" />}

          {/* AI-generated TL;DR summary */}
          {blog.ai_summary && (
            <div
              className="mt-8 rounded-2xl border border-[#F39200]/30 bg-gradient-to-br from-[#F39200]/10 via-[#F39200]/5 to-transparent p-5 md:p-6"
              data-testid="blog-ai-summary"
            >
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#F39200] font-bold mb-2">
                <Sparkles size={14} /> AI summary
              </div>
              <p className="text-[15px] md:text-base leading-relaxed text-neutral-800">
                {blog.ai_summary}
              </p>
            </div>
          )}

          {/* Summary / Table of Contents */}
          {headings.length >= 2 && (
            <div className="mt-8 bg-[#FAF2E8]/60 border border-black/5 rounded-2xl p-5" data-testid="blog-toc">
              <div className="text-xs uppercase tracking-wider text-[#F39200] font-semibold mb-3">In this article</div>
              <ol className="list-decimal list-inside space-y-1.5 text-sm text-neutral-800 marker:text-[#F39200] marker:font-bold">
                {headings.map((h, i) => (
                  <li key={i}><a href={`#${h.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'')}`} className="hover:text-[#F39200]">{h}</a></li>
                ))}
              </ol>
            </div>
          )}

          <div className="prose prose-neutral max-w-none mt-10 prose-headings:font-extrabold prose-a:text-[#F39200] prose-img:rounded-xl">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{split ? split.before : blog.body}</ReactMarkdown>
          </div>

          {/* Mid-post CTA banner */}
          <BlogCTABanner testId="blog-cta-mid" />

          {split && (
            <div className="prose prose-neutral max-w-none prose-headings:font-extrabold prose-a:text-[#F39200] prose-img:rounded-xl">
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{split.after}</ReactMarkdown>
            </div>
          )}

          {blog.tags && blog.tags.length > 0 && (
            <div className="mt-10 pt-6 border-t border-black/5 flex flex-wrap gap-2">
              {blog.tags.map((t) => (
                <span key={t} className="text-xs font-semibold px-3 py-1 rounded-full bg-[#F39200]/10 text-[#F39200]">#{t}</span>
              ))}
            </div>
          )}

          {/* End-of-post CTA banner */}
          <BlogCTABanner testId="blog-cta-end" />
        </div>
      </article>

      {/* Related posts */}
      {related.length > 0 && (
        <section className="bg-[#FAF2E8] py-14" data-testid="blog-related">
          <div className="max-w-7xl mx-auto px-5 lg:px-10">
            <div className="text-xs uppercase tracking-wider text-[#F39200] font-semibold mb-3">Keep reading</div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-neutral-900">Related articles</h2>
            <div className="mt-8 grid md:grid-cols-3 gap-6">
              {related.map((b) => (
                <Link key={b.id} to={`/blog/${b.slug}`} data-testid={`related-post-${b.slug}`} className="group bg-white border border-black/5 rounded-2xl overflow-hidden hover:border-[#F39200] hover:shadow-lg transition-all flex flex-col">
                  {b.cover_image_url ? (
                    <div className="aspect-[16/9] overflow-hidden bg-neutral-100"><img src={absoluteUploadUrl(b.cover_image_url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                  ) : (
                    <div className="aspect-[16/9] bg-gradient-to-br from-[#F39200]/20 via-[#F39200]/5 to-transparent" />
                  )}
                  <div className="p-5 flex-1 flex flex-col">
                    {b.tags?.[0] && <span className="text-[10px] uppercase tracking-wider text-[#F39200] font-semibold">{b.tags[0]}</span>}
                    <h3 className="mt-1 text-base font-bold text-neutral-900 group-hover:text-[#F39200] transition-colors leading-snug line-clamp-2">{b.title}</h3>
                    {b.excerpt && <p className="mt-2 text-xs text-neutral-600 line-clamp-2">{b.excerpt}</p>}
                    <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#F39200]">Read <ArrowRight size={12} /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Popular searches */}
      {popularTags.length > 0 && (
        <section className="bg-[#FFF8ED] py-14" data-testid="popular-searches">
          <div className="max-w-7xl mx-auto px-5 lg:px-10">
            <div className="text-xs uppercase tracking-wider text-[#F39200] font-semibold mb-3 flex items-center gap-2"><Hash size={12}/> Popular searches</div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-neutral-900 mb-6">What readers are looking for</h2>
            <div className="flex flex-wrap gap-2.5">
              {popularTags.map((t) => (
                <Link
                  key={t}
                  to={`/blog?tag=${encodeURIComponent(t)}`}
                  data-testid={`popular-tag-${t.toLowerCase().replace(/\s+/g,'-')}`}
                  className="text-sm px-4 py-2 rounded-full bg-[#FAF2E8] border border-black/5 text-neutral-800 hover:border-[#F39200] hover:text-[#F39200] transition-colors"
                >
                  {t}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <ContactSection />
      <Footer />
    </div>
  );
}
